import { storyblokEditable, type ISbStoryData } from '@storyblok/react';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';

// ============================================================================
// Storyblok rich text node renderer
// https://www.storyblok.com/docs/richtext-field
// ============================================================================

type RichTextMark = {
  type: string;
  attrs?: { href?: string; target?: string; linktype?: string };
};

type RichTextNode = {
  type: string;
  text?: string;
  marks?: RichTextMark[];
  attrs?: Record<string, unknown>;
  content?: RichTextNode[];
};

const applyMarks = (text: string, marks?: RichTextMark[]): ReactNode => {
  if (!marks || marks.length === 0) {
    return text;
  }

  return marks.reduce<ReactNode>((node, mark) => {
    switch (mark.type) {
      case 'bold':
        return <strong>{node}</strong>;
      case 'italic':
        return <em>{node}</em>;
      case 'underline':
        return <u>{node}</u>;
      case 'strike':
        return <s>{node}</s>;
      case 'code':
        return <code className="rounded bg-surface-sunken text-sm">{node}</code>;
      case 'link': {
        const href = mark.attrs?.href ?? '';
        const isExternal =
          typeof href === 'string' && (href.startsWith('http') || mark.attrs?.linktype === 'url');
        if (isExternal) {
          return (
            <a
              href={href}
              target={mark.attrs?.target ?? '_blank'}
              rel="noopener noreferrer"
              className="underline"
            >
              {node}
            </a>
          );
        }
        return (
          <Link to={href} className="underline">
            {node}
          </Link>
        );
      }
      default:
        return node;
    }
  }, text);
};

const renderRichTextNode = (node: RichTextNode, key?: number): ReactNode => {
  const classValues = {
    'text-center': node.attrs?.['textAlign'] === 'center',
    'text-justify': node.attrs?.['textAlign'] === 'justify',
  };

  switch (node.type) {
    case 'doc':
      return (
        <React.Fragment key={key}>
          {node.content?.map((child, i) => renderRichTextNode(child, i))}
        </React.Fragment>
      );
    case 'paragraph': {
      // A paragraph containing only image nodes should not be wrapped in <p>
      // (invalid HTML, and images need to be block-level)
      const children = node.content ?? [];
      const isImageOnly = children.length > 0 && children.every((c) => c.type === 'image');
      if (isImageOnly) {
        return (
          <React.Fragment key={key}>
            {children.map((child, i) => renderRichTextNode(child, i))}
          </React.Fragment>
        );
      }
      return (
        <p key={key} className={clsx(classValues)}>
          {children.map((child, i) => renderRichTextNode(child, i))}
        </p>
      );
    }
    case 'text':
      return <span key={key}>{applyMarks(node.text ?? '', node.marks)}</span>;
    case 'hard_break':
      return <br key={key} />;
    case 'horizontal_rule':
      return <hr key={key} className="border-border" />;
    case 'heading': {
      const levelRaw = node.attrs?.['level'];
      const level = typeof levelRaw === 'number' ? levelRaw : 2;
      const className = clsx('font-medium text-text', {
        'text-2xl': level === 1,
        'text-xl': level === 2,
        'text-lg': level === 3,
        'text-base': level >= 4,
        ...classValues,
      });
      const children = node.content?.map((child, i) => renderRichTextNode(child, i));
      switch (level) {
        case 1:
          return (
            <h1 key={key} className={className}>
              {children}
            </h1>
          );
        case 2:
          return (
            <h2 key={key} className={className}>
              {children}
            </h2>
          );
        case 3:
          return (
            <h3 key={key} className={className}>
              {children}
            </h3>
          );
        case 4:
          return (
            <h4 key={key} className={className}>
              {children}
            </h4>
          );
        case 5:
          return (
            <h5 key={key} className={className}>
              {children}
            </h5>
          );
        default:
          return (
            <h6 key={key} className={className}>
              {children}
            </h6>
          );
      }
    }
    case 'bullet_list':
      return (
        <ul key={key} className="list-disc">
          {node.content?.map((child, i) => renderRichTextNode(child, i))}
        </ul>
      );
    case 'ordered_list':
      return (
        <ol key={key} className="list-decimal">
          {node.content?.map((child, i) => renderRichTextNode(child, i))}
        </ol>
      );
    case 'list_item':
      return <li key={key}>{node.content?.map((child, i) => renderRichTextNode(child, i))}</li>;
    case 'blockquote':
      return (
        <blockquote key={key} className="border-l-4 border-border italic text-text-secondary">
          {node.content?.map((child, i) => renderRichTextNode(child, i))}
        </blockquote>
      );
    case 'code_block':
      return (
        <pre key={key} className="overflow-x-auto rounded bg-surface-sunken text-sm">
          <code>{node.content?.map((child, i) => renderRichTextNode(child, i))}</code>
        </pre>
      );
    case 'image': {
      const srcRaw = node.attrs?.['src'];
      const altRaw = node.attrs?.['alt'];
      const src = typeof srcRaw === 'string' ? srcRaw : undefined;
      const alt = typeof altRaw === 'string' ? altRaw : undefined;
      if (!src) {
        return null;
      }
      return <img key={key} src={src} alt={alt} className="max-w-full rounded-lg" />;
    }
    case 'blok': {
      if (!node.attrs || !Array.isArray(node.attrs['body'])) {
        return null;
      }
      return renderBlocks(node.attrs['body'] as BlockBase[]);
    }
    default:
      return null;
  }
};

const isRichTextDoc = (value: unknown): value is RichTextNode =>
  typeof value === 'object' && value !== null && 'type' in value;

// ============================================================================
// Block types from Storyblok
// ============================================================================

type BlockBase = {
  _uid: string;
  _editable?: string;
  component: string;
};

type TextBlock = BlockBase & {
  component: 'text';
  children?: unknown; // Storyblok rich text doc node
  className?: string;
};

type GridBlock = BlockBase & {
  component: 'grid';
  columns?: number;
  className?: string;
  children?: BlockBase[];
};

type BoxBlock = BlockBase & {
  component: 'box';
  className?: string;
  children?: BlockBase[];
};

type PageBlock = BlockBase & {
  component: 'page';
  children?: BlockBase[];
  className?: string;
};

type ImageBlock = BlockBase & {
  component: 'image';
  /**
   * @deprecated Use `image.filename` instead.
   */
  src?: string;
  /**
   * @deprecated Use `image.alt` instead.
   */
  alt?: string;
  image?: { filename: string; alt: string };
  className?: string;
};

type AudioBlock = BlockBase & {
  component: 'audio';
  audio?: { alt: string; filename: string };
  className?: string;
};

type VideoBlock = BlockBase & {
  component: 'video';
  video?: { alt: string; filename: string };
  className?: string;
};

type YoutubeVideoBlock = BlockBase & {
  component: 'youtube_video';
  video_id?: string;
  className?: string;
  is_vertical?: boolean;
};

type ButtonBlock = BlockBase & {
  component: 'button';
  /**
   * @deprecated Use `link` instead.
   */
  href?: string;
  link?: { url: string; linktype: 'url' | 'story'; cached_url: string };
  children?: string;
  className?: string;
};

type LinkBlock = BlockBase & {
  component: 'link';
  /**
   * @deprecated Use `link` instead.
   */
  href?: string;
  link?: { url: string; linktype: 'url' | 'story'; cached_url: string };
  children?: string;
  className?: string;
};

type CardBlock = BlockBase & {
  component: 'card';
  className?: string;
  children?: BlockBase[];
};

type Block =
  | TextBlock
  | GridBlock
  | BoxBlock
  | PageBlock
  | ImageBlock
  | AudioBlock
  | VideoBlock
  | YoutubeVideoBlock
  | ButtonBlock
  | LinkBlock
  | CardBlock
  | BlockBase;

// Type guards
const isPageBlock = (b: BlockBase): b is PageBlock => b.component === 'page';
const isTextBlock = (b: BlockBase): b is TextBlock => b.component === 'text';
const isGridBlock = (b: BlockBase): b is GridBlock => b.component === 'grid';
const isBoxBlock = (b: BlockBase): b is BoxBlock => b.component === 'box';
const isImageBlock = (b: BlockBase): b is ImageBlock => b.component === 'image';
const isAudioBlock = (b: BlockBase): b is AudioBlock => b.component === 'audio';
const isVideoBlock = (b: BlockBase): b is VideoBlock => b.component === 'video';
const isYoutubeVideoBlock = (b: BlockBase): b is YoutubeVideoBlock =>
  b.component === 'youtube_video';
const isButtonBlock = (b: BlockBase): b is ButtonBlock => b.component === 'button';
const isLinkBlock = (b: BlockBase): b is LinkBlock => b.component === 'link';
const isCardBlock = (b: BlockBase): b is CardBlock => b.component === 'card';

const isBlock = (value: unknown): value is Block =>
  typeof value === 'object' && value !== null && '_uid' in value && 'component' in value;

// ============================================================================
// Block components
// ============================================================================

const renderBlocks = (blocks?: BlockBase[]): ReactNode => blocks?.map(renderBlock) ?? null;

const renderBlock = (block: Block): ReactNode => {
  if (isPageBlock(block)) {
    if (!block.children?.length) {
      return null;
    }
    return (
      <section
        key={block._uid}
        {...storyblokEditable(block)}
        className={clsx('prose', block.className)}
      >
        {renderBlocks(block.children)}
      </section>
    );
  }

  if (isTextBlock(block)) {
    if (!block.children) {
      return null;
    }
    return (
      <div
        key={block._uid}
        {...storyblokEditable(block)}
        className={clsx('max-w-prose grid mx-auto', block.className)}
      >
        {isRichTextDoc(block.children)
          ? // Storyblok rich text is a doc node tree, not an HTML string
            renderRichTextNode(block.children)
          : // Fallback: plain string (shouldn't occur in normal usage)
            JSON.stringify(block.children)}
      </div>
    );
  }

  if (isGridBlock(block)) {
    const columns = block.columns ?? 1;
    return (
      <div
        key={block._uid}
        {...storyblokEditable(block)}
        className={clsx('grid gap-4', block.className)}
        style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
      >
        {renderBlocks(block.children)}
      </div>
    );
  }

  if (isBoxBlock(block)) {
    return (
      <div key={block._uid} {...storyblokEditable(block)} className={clsx(block.className)}>
        {renderBlocks(block.children)}
      </div>
    );
  }

  if (isImageBlock(block)) {
    const src = block.src ?? block.image?.filename;
    if (!src) {
      return null;
    }
    const alt = block.alt ?? block.image?.alt;
    return (
      <img
        key={block._uid}
        {...storyblokEditable(block)}
        src={src}
        alt={alt}
        className={clsx('max-w-full rounded-lg', block.className)}
      />
    );
  }

  if (isAudioBlock(block)) {
    if (!block.audio?.filename) {
      return null;
    }
    return (
      <audio
        key={block._uid}
        {...storyblokEditable(block)}
        className={clsx('w-full', block.className)}
        controls
        controlsList="nofullscreen nodownload noplaybackrate noremoteplayback"
      >
        <source src={block.audio.filename} />
        Your browser does not support the audio element.
      </audio>
    );
  }

  if (isVideoBlock(block)) {
    if (!block.video?.filename) {
      return null;
    }
    return (
      <video
        key={block._uid}
        {...storyblokEditable(block)}
        className={clsx('w-full', block.className)}
        controls
        controlsList="nofullscreen nodownload noplaybackrate noremoteplayback"
      >
        <source src={block.video.filename} />
        Your browser does not support the video element.
      </video>
    );
  }

  if (isYoutubeVideoBlock(block)) {
    if (!block.video_id) {
      return null;
    }
    return (
      <div
        key={block._uid}
        {...storyblokEditable(block)}
        className={clsx(
          'w-full',
          { 'aspect-video': !block.is_vertical, 'aspect-[9/16]': block.is_vertical },
          block.className,
        )}
      >
        <iframe
          src={`https://www.youtube.com/embed/${block.video_id}`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="h-full w-full rounded-lg"
        />
      </div>
    );
  }

  if (isButtonBlock(block)) {
    const href = block.href ?? block.link?.cached_url ?? block.link?.url ?? '#';
    const isExternal = href.startsWith('http') || block.link?.linktype === 'url';
    const baseClass = clsx(
      'inline-flex items-center justify-center rounded bg-button-solid-bg px-4 py-2 text-sm font-medium text-text-inverse no-underline transition-opacity hover:opacity-75',
      block.className,
    );
    if (isExternal) {
      return (
        <a
          key={block._uid}
          {...storyblokEditable(block)}
          href={href}
          className={baseClass}
          target="_blank"
          rel="noopener noreferrer"
        >
          {block.children ?? 'Learn more'}
        </a>
      );
    }
    return (
      <Link key={block._uid} {...storyblokEditable(block)} to={href} className={baseClass}>
        {block.children ?? 'Learn more'}
      </Link>
    );
  }

  if (isLinkBlock(block)) {
    const href = block.href ?? block.link?.cached_url ?? block.link?.url ?? '#';
    const isExternal = href.startsWith('http') || block.link?.linktype === 'url';
    const className = clsx('underline', block.className);
    if (isExternal) {
      return (
        <a
          key={block._uid}
          {...storyblokEditable(block)}
          href={href}
          className={className}
          target="_blank"
          rel="noopener noreferrer"
        >
          {block.children ?? href}
        </a>
      );
    }
    return (
      <Link key={block._uid} {...storyblokEditable(block)} to={href} className={className}>
        {block.children ?? href}
      </Link>
    );
  }

  if (isCardBlock(block)) {
    return (
      <div
        key={block._uid}
        {...storyblokEditable(block)}
        className={clsx(
          'rounded-xl border border-border bg-surface p-4 shadow-sm',
          block.className,
        )}
      >
        {renderBlocks(block.children)}
      </div>
    );
  }

  return null;
};

// ============================================================================
// Main export
// ============================================================================

type CmsContentProps = {
  story: ISbStoryData;
};

export const CmsContent = ({ story }: CmsContentProps) => {
  if (!isBlock(story.content)) {
    return null;
  }
  return renderBlock(story.content);
};
